import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import ScrollToTop from '../../../common/widgets/ScrollToTop';
import { WithTabTitle } from '../../../common/widgets/withTabTitle';
import NotFoundPage from '../Pages/404Page';
import CartPage from '../Pages/CartPage';
import { CartSuccessPage } from '../Pages/CartPage/Success';
import { ContestsPage } from '../Pages/ContestsPage';
import { ContestDetailsPage } from '../Pages/ContestsPage/ContestDetailsPage';
import DemoPage from '../Pages/DemoPage';
import HomePage from '../Pages/HomePage';
import { CustomerInfoContext } from '../contexts/CustomerInfoContext';
import { CustomerPermissionsProvider } from '../contexts/CustomerPermissionsContext';
import { TopLevelCategories } from '../enums';
import BenefitLayout from '../layout/layouts/BenefitLayout';
import { resources } from '../resources/resources';
import { AdminAccountRoutes } from './AdminAccount';
import { DiscoverRoutes } from './Discover';
import { BenefitsRoutes } from './FlexibleBenefits';
import { HelpRoutes } from './Help';
import MyAccountRoutes from './MyAccount';
import { ProductsRoutes } from './Products';
import PrivateRoute from './privateRoute';

export function Routing() {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route
          path=""
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.home)}>
                <HomePage />
              </WithTabTitle>
            </BenefitLayout>
          }
        />
        <Route
          path="my-account/*"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.account)}>
                <MyAccountRoutes />
              </WithTabTitle>
            </BenefitLayout>
          }
        />
        <Route
          path="my-benefits"
          element={
            <Navigate
              to={{
                pathname: '/my-account/my-benefits',
                search: location.search,
                hash: location.hash,
              }}
              state={location.state}
              replace
            />
          }
        />

        <Route
          path="cart"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.cart)}>
                <CartPage />
              </WithTabTitle>
            </BenefitLayout>
          }
        />

        <Route
          path="cart-success"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.cart)}>
                <CartSuccessPage />
              </WithTabTitle>
            </BenefitLayout>
          }
        />

        <Route path="products/*" element={<ProductsRoutes />} />

        {process.env.NODE_ENV === 'development' && (
          <Route
            path="demo"
            element={
              <BenefitLayout>
                <DemoPage />
              </BenefitLayout>
            }
          />
        )}
        <Route
          path="flexible-benefits"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.flexibleBenefits)}>
                <BenefitsRoutes
                  topLevelCategoryId={TopLevelCategories.FlexibleBenefits}
                />
              </WithTabTitle>
            </BenefitLayout>
          }
        ></Route>
        <Route
          path="flexible-benefits/*"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.flexibleBenefits)}>
                <BenefitsRoutes
                  topLevelCategoryId={TopLevelCategories.FlexibleBenefits}
                />
              </WithTabTitle>
            </BenefitLayout>
          }
        ></Route>
        <Route
          path="club-benefits/*"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.benefitClub)}>
                <BenefitsRoutes
                  topLevelCategoryId={TopLevelCategories.BenefitClub}
                />
              </WithTabTitle>
            </BenefitLayout>
          }
        ></Route>
        <Route
          path="discount-vouchers/*"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.discountVouchers)}>
                <BenefitsRoutes
                  topLevelCategoryId={TopLevelCategories.DiscountVouchers}
                />
              </WithTabTitle>
            </BenefitLayout>
          }
        ></Route>
        <Route
          path="cashback/*"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.menu.cashback)}>
                <BenefitsRoutes
                  topLevelCategoryId={TopLevelCategories.Cashback}
                />
              </WithTabTitle>
            </BenefitLayout>
          }
        ></Route>

        <Route path="discover/*" element={<DiscoverRoutes />} />

        <Route
          path="help/*"
          element={
            <BenefitLayout>
              <HelpRoutes />
            </BenefitLayout>
          }
        />
        <Route
          element={<PrivateRoute isPowerUser={customerInfo.isPowerUser} />}
        >
          <Route
            path="admin/*"
            element={
              <BenefitLayout>
                <CustomerPermissionsProvider>
                  <AdminAccountRoutes />
                </CustomerPermissionsProvider>
              </BenefitLayout>
            }
          />
        </Route>
        <Route
          path="contests"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.contests.contests)}>
                <ContestsPage />
              </WithTabTitle>
            </BenefitLayout>
          }
        />
        <Route
          path="contests/:contestId"
          element={
            <BenefitLayout>
              <WithTabTitle title={translate(resources.contests.contests)}>
                <ContestDetailsPage />
              </WithTabTitle>
            </BenefitLayout>
          }
        />
        <Route
          path="*"
          element={
            <BenefitLayout>
              <NotFoundPage />
            </BenefitLayout>
          }
        ></Route>
      </Routes>
    </>
  );
}
